import { useState, useEffect } from 'react';
import { hoyISO, primerDiaMesISO } from '../utils/fechaHoy';
import FiltroUsuarios from './FiltroUsuarios';
import { useFiltroUsuarios } from '../filtroUsuarios';
import { getUsuarioActual } from '../apiAuth';

const API = '/api';

function formatoDuracion(minutos) {
  if (minutos === null || minutos === undefined) return 'En curso';
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return `${h}h ${m}m`;
}

export default function ReportesAcceso() {
  const [fechaInicio, setFechaInicio] = useState(primerDiaMesISO());
  const [fechaFin, setFechaFin] = useState(hoyISO());
  const [usuarioId, setUsuarioId] = useState('todos');
  const [usuarios, setUsuarios] = useState([]);
  const [filas, setFilas] = useState([]);
  const [buscado, setBuscado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Administrador Plus: en vez del selector de un usuario (que lista a todos),
  // usa <FiltroUsuarios> con solo sus usuarios asignados; el backend acota igual.
  const esAdminPlus = getUsuarioActual()?.rol === 'administrador_plus';
  const filtroUsuariosKey = useFiltroUsuarios().join(',');

  useEffect(() => {
    if (esAdminPlus) return;
    fetch(`${API}/usuarios`)
      .then(r => r.json())
      .then(data => setUsuarios(Array.isArray(data) ? data : []))
      .catch(() => setUsuarios([]));
  }, []);

  const buscar = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ fecha_inicio: fechaInicio, fecha_fin: fechaFin, usuario_id: usuarioId });
      const r = await fetch(`${API}/reportes-acceso?${params.toString()}`);
      const data = await r.json();
      if (!r.ok) {
        setError(data.error || 'Error al cargar el reporte');
        setFilas([]);
        return;
      }
      setFilas(data);
      setBuscado(true);
    } catch {
      setError('Error de conexión con el servidor');
      setFilas([]);
    } finally {
      setLoading(false);
    }
  };

  // Si ya se buscó, al cambiar el filtro de usuarios se vuelve a buscar.
  useEffect(() => {
    if (buscado) buscar();
  }, [filtroUsuariosKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportar = async () => {
    try {
      const params = new URLSearchParams({ fecha_inicio: fechaInicio, fecha_fin: fechaFin, usuario_id: usuarioId });
      const r = await fetch(`${API}/reportes-acceso/exportar?${params.toString()}`);
      if (!r.ok) return;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'reporte-acceso.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Error al exportar el reporte');
    }
  };

  const label = { display: 'block', marginBottom: 4, fontSize: 13, color: '#4b5563' };
  const input = { padding: 8, border: '1px solid #d1d5db', borderRadius: 4, minWidth: 140 };

  const totalAccesos = filas.length;
  const tiempoPorUsuario = Object.entries(
    filas.reduce((acc, f) => {
      if (!acc[f.usuario_id]) acc[f.usuario_id] = { nombre: f.nombre_usuario, minutos: 0 };
      acc[f.usuario_id].minutos += f.duracion_minutos || 0;
      return acc;
    }, {})
  ).map(([id, u]) => ({ id, ...u }));

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ color: '#1a3a6e', marginBottom: 16 }}>Reportes de Ingreso a Plataforma</h2>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20, background: 'white', padding: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={label}>Fecha inicio</label>
          <input type="date" style={input} value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
        </div>
        <div>
          <label style={label}>Fecha fin</label>
          <input type="date" style={input} value={fechaFin} onChange={e => setFechaFin(e.target.value)} />
        </div>
        {esAdminPlus ? (
          <FiltroUsuarios ancho={260} />
        ) : (
          <div>
            <label style={label}>Usuario</label>
            <select style={input} value={usuarioId} onChange={e => setUsuarioId(e.target.value)}>
              <option value="todos">Todos</option>
              {usuarios.map(u => (
                <option key={u.id} value={u.id}>{u.nombre_completo || u.usuario}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <button
            onClick={buscar}
            disabled={loading}
            style={{ padding: '9px 20px', border: 'none', borderRadius: 4, backgroundColor: '#1a3a6e', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
          >
            {loading ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
        {buscado && filas.length > 0 && (
          <div>
            <button
              onClick={exportar}
              style={{ padding: '9px 20px', border: '1px solid #1a3a6e', borderRadius: 4, backgroundColor: 'white', color: '#1a3a6e', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Exportar a Excel
            </button>
          </div>
        )}
      </div>

      {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}

      <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
              <th style={{ padding: 10 }}>Usuario</th>
              <th style={{ padding: 10 }}>Fecha</th>
              <th style={{ padding: 10 }}>Hora Inicio</th>
              <th style={{ padding: 10 }}>Hora Fin</th>
              <th style={{ padding: 10 }}>Duración</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>Cargando...</td></tr>
            )}
            {!loading && buscado && filas.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>Sin accesos para el periodo seleccionado</td></tr>
            )}
            {!loading && !buscado && (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>Seleccione un rango de fechas y presione "Buscar"</td></tr>
            )}
            {!loading && filas.map(f => (
              <tr key={f.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                <td style={{ padding: 10 }}>{f.nombre_usuario}</td>
                <td style={{ padding: 10 }}>{f.fecha}</td>
                <td style={{ padding: 10 }}>{f.hora_inicio}</td>
                <td style={{ padding: 10 }}>{f.hora_fin || '—'}</td>
                <td style={{ padding: 10 }}>{formatoDuracion(f.duracion_minutos)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && buscado && filas.length > 0 && (
        <div style={{ marginTop: 20, background: 'white', padding: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ fontWeight: 'bold', color: '#1a3a6e', marginBottom: 8 }}>
            Total de accesos en el período: {totalAccesos}
          </div>
          <div style={{ fontWeight: 'bold', color: '#1a3a6e', marginBottom: 4 }}>Tiempo total de uso por usuario:</div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {tiempoPorUsuario.map(u => (
              <li key={u.id} style={{ fontSize: 14, color: '#374151' }}>
                {u.nombre}: {formatoDuracion(u.minutos)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
